import { isSlovakIban, isValidIban, tt } from '@agos/shared'

/**
 * The Tatra Banka PSD2 configuration, and the fact that it cannot pay anybody.
 *
 * `BLOCKED.md` records this already, and the frontend's job is to say it on the screen
 * rather than leave a scheduler button looking functional:
 *
 * > `POST /api/tatrabanka/run-scheduler` and the *Naplánovať automatickú platbu* button
 * > have **never paid anybody**. The legacy PSD2 client is an explicit stub… the
 * > scheduler finds the due invoices, records a **failed** payment attempt against each
 * > one carrying the message *"TODO: Tatrabanka PSD2 klient nie je implementovaný"*, and
 * > leaves them unpaid.
 *
 * The path that works is the SEPA batch on `/invoices` — an XML file the owner uploads
 * to their own business banking and approves there. **FB-084** is that legacy's page
 * gives no sign of the difference: it offers an *Enabled* toggle, credential fields, a
 * *run now* button and a numbered list that presents the two approaches as equals.
 *
 * The credentials themselves are configured for real, because they are what the working
 * class will need the day the developer agreement exists. Only the promise is corrected.
 */

export interface BankSettings {
  enabled?: boolean
  clientId?: string | null
  /** Write-only: the API says whether one is stored, never what it is. */
  clientSecretSet?: boolean
  ibanFrom?: string | null
  sandbox?: boolean
  minAdvanceMinutes?: number
}

export interface BankForm {
  enabled: boolean
  clientId: string
  /** Empty means "leave the stored one alone". */
  clientSecret: string
  ibanFrom: string
  sandbox: boolean
  minAdvanceMinutes: string
}

/** Legacy's default: an hour of lead time before a due date. */
export const DEFAULT_MIN_ADVANCE_MINUTES = 60

export function formOf(settings: BankSettings | undefined): BankForm {
  return {
    enabled: settings?.enabled === true,
    clientId: settings?.clientId ?? '',
    clientSecret: '',
    ibanFrom: settings?.ibanFrom ?? '',
    sandbox: settings?.sandbox !== false,
    minAdvanceMinutes: String(settings?.minAdvanceMinutes ?? DEFAULT_MIN_ADVANCE_MINUTES),
  }
}

export type SettingProblem =
  | { kind: 'no-client-id' }
  | { kind: 'no-secret' }
  | { kind: 'no-iban' }
  | { kind: 'iban-checksum'; value: string }
  | { kind: 'iban-length'; value: string }
  | { kind: 'advance-not-a-number'; value: string }
  | { kind: 'advance-negative'; value: number }

/**
 * What is missing or wrong — reported, never blocking a save.
 *
 * Half-configured is a legitimate state: the credentials arrive from the bank one at a
 * time, and somebody pasting a client id today should be able to keep it. The IBAN is
 * checked with the same mod-97 the supplier editor uses (FB-080) — it is the account
 * money would leave from, so a transposed digit matters at least as much.
 */
export function settingProblems(form: BankForm, secretStored: boolean): SettingProblem[] {
  const problems: SettingProblem[] = []

  if (form.enabled) {
    if (form.clientId.trim() === '') problems.push({ kind: 'no-client-id' })
    if (!secretStored && form.clientSecret.trim() === '') problems.push({ kind: 'no-secret' })
    if (form.ibanFrom.trim() === '') problems.push({ kind: 'no-iban' })
  }

  const iban = form.ibanFrom.trim()
  if (iban !== '') {
    if (!isValidIban(iban)) {
      problems.push({ kind: 'iban-checksum', value: iban })
    } else if (iban.replace(/\s/g, '').toUpperCase().startsWith('SK') && !isSlovakIban(iban)) {
      problems.push({ kind: 'iban-length', value: iban })
    }
  }

  const advance = form.minAdvanceMinutes.trim()
  if (advance !== '') {
    const minutes = Number(advance)
    if (!Number.isFinite(minutes)) {
      problems.push({ kind: 'advance-not-a-number', value: advance })
    } else if (minutes < 0) {
      problems.push({ kind: 'advance-negative', value: minutes })
    }
  }

  return problems
}

/** Nothing here refuses a save; a half-filled configuration is a real state. */
export function canSave(problems: SettingProblem[]): boolean {
  return !problems.some(
    (problem) =>
      problem.kind === 'advance-not-a-number' ||
      problem.kind === 'iban-checksum' ||
      problem.kind === 'iban-length',
  )
}

/**
 * The body `PATCH /tatrabanka/settings` wants.
 *
 * An untouched secret box sends **no** `clientSecret` at all — sending `''` would clear
 * a stored credential, the same trap the supplier editor's API key has (FB-051's class).
 */
export function settingsPayload(form: BankForm): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    enabled: form.enabled,
    clientId: form.clientId.trim() || null,
    // Stored unspaced so one account is one string.
    ibanFrom: form.ibanFrom.replace(/\s/g, '').toUpperCase() || null,
    sandbox: form.sandbox,
    minAdvanceMinutes: Number(form.minAdvanceMinutes.trim()) || DEFAULT_MIN_ADVANCE_MINUTES,
  }
  const secret = form.clientSecret.trim()
  if (secret) payload.clientSecret = secret
  return payload
}

export interface SchedulerOutcome {
  scanned: number
  paid: number
  failed: number
  skipped: string | null
}

export function readOutcome(result: unknown): SchedulerOutcome {
  const record = typeof result === 'object' && result !== null ? (result as Record<string, unknown>) : {}
  const number = (value: unknown) => (typeof value === 'number' ? value : 0)
  const skipped = record.skipped
  return {
    scanned: number(record.scanned),
    paid: number(record.paid),
    failed: number(record.failed),
    skipped: typeof skipped === 'string' && skipped !== '' ? skipped : null,
  }
}

/**
 * What a scheduler run actually means, said in one sentence.
 *
 * **FB-084.** A run that scans invoices and pays none of them is the *designed*
 * behaviour, not a transient failure, so "0 paid" must not read as bad luck. And a run
 * that reports payments would mean the stub had been replaced — worth noticing rather
 * than assuming.
 */
export function outcomeMessage(outcome: SchedulerOutcome): string {
  if (outcome.skipped !== null) return tt('Plánovač sa nespustil.')
  if (outcome.paid > 0) return tt('Platby prebehli - PSD2 klient je teda už implementovaný.')
  if (outcome.scanned === 0) return tt('Žiadne faktúry neboli na zaplatenie.')
  return tt(
    'Ani jedna faktúra nebola zaplatená - PSD2 klient je zámerne nedokončený, takže každý pokus skončí chybou. Použite SEPA dávku vo Faktúrach.',
  )
}

/** Whether the outcome is the expected non-payment or something new. */
export function outcomeIsExpected(outcome: SchedulerOutcome): boolean {
  return outcome.paid === 0
}
